// Light-DOM card helpers: for cards that render the shop's OWN markup (slotted into the page, so
// the shop's CSS and JS apply) instead of the SDK's shadow-DOM default card.

import { SlottedMarkerAttribute, type CardButton } from "@raventic/sdk-common";
import type { JSX } from "preact";
import { joinClasses } from "./helpers/text.ts";

// Spread onto an element inside a card to mark it as slotted content: the card host's click
// handler (`originatesInSlot`) then leaves clicks inside it alone (no card tracking / navigation).
export const slottedMarker = { [SlottedMarkerAttribute]: "" } as const;

// For a control nested in the card's `<a href>`: prevents a click on the control's OWN box (its
// padding) from following the anchor, while a click on a child (the submit button) keeps its native
// behavior.
export const preventOwnDefault = (e: Event): void => {
  if (e.target === e.currentTarget) e.preventDefault();
};

type Attributes = Record<string, string | boolean | undefined>;

export type HostCartForm = {
  // The shop's add-to-cart endpoint.
  action: string;
  method?: "post" | "get"; // default "post"
  // Extra `<form>` attributes (className, name, data-*, noValidate, …).
  attributes?: Attributes;
  // Submit `<button>` attributes (className, name, data-*, …). Its label is the SDK's `button.label`.
  submit?: Attributes;
  // Hidden inputs (product id, quantity, …), in order.
  inputs: readonly { name: string; value: string; id?: string; className?: string }[];
};

export type HostCartControlProps = {
  button: CardButton;
  // The shop's add-to-cart form for the `cart` action; null → no control on a cart button (e.g. the
  // product lacks the shop id the form needs).
  form: HostCartForm | null;
  // Classes of the SDK control rendered for every OTHER action (detail link, …); `button.classes` is
  // appended.
  className?: string;
};

// The card button, in the shop's markup. `cart` action → the shop's own add-to-cart `<form>`, which
// submits natively (the shop's JS can hijack it as on its own listing); the SDK's `onClick` + pending
// state are wired onto the submit button only in Form element mode (`button.form !== null`). Any other
// action → the SDK control (`button.getProps()`) as an `<a>` (with an href) or `<span>`, with
// `className`. Both are marked slotted and sit safely inside the card's anchor.
export const HostCartControl = ({ button, form, className }: HostCartControlProps): JSX.Element | null => {
  if (button.action === "cart") {
    if (form === null) return null;
    const sdkProps = button.form !== null ? button.getProps() : null;
    return (
      <form method={form.method ?? "post"} action={form.action} onClick={preventOwnDefault} {...form.attributes} {...slottedMarker}>
        <button
          type="submit"
          {...form.submit}
          {...(sdkProps && { onClick: sdkProps.onClick, disabled: sdkProps.disabled, "aria-busy": sdkProps["aria-busy"] as boolean | undefined })}
        >
          {button.label}
        </button>
        {form.inputs.map((input) => (
          <input key={input.name} type="hidden" id={input.id} name={input.name} className={input.className} value={input.value} />
        ))}
      </form>
    );
  }

  const controlProps = button.getProps();
  const Element = controlProps.href !== undefined ? "a" : "span";
  return (
    <Element
      className={joinClasses(className, button.classes)}
      {...slottedMarker}
      {...controlProps}
      onClick={(e: Event) => {
        preventOwnDefault(e);
        controlProps.onClick(e);
      }}
    >
      {button.label}
    </Element>
  );
};
